<template>
  <v-main>
    <v-container fluid>
      <!-- Header Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-title>
              <h3 class="font-weight-bold">Colors</h3>
              <v-btn
                class="mx-2 mb-2"
                fab
                x-small
                color="primary"
                @click="openCreateDialog"
              >
                <v-icon dark>mdi-plus</v-icon>
              </v-btn>
            </v-card-title>
            <v-card-text>
              <v-row>
                <v-col md="8">
                  <v-text-field
                    solo
                    rounded
                    v-model="filter.search_keyword"
                    label="Search colors..."
                    prepend-inner-icon="mdi-magnify"
                    clearable
                    @click:clear="(filter.search_keyword = null), getData()"
                    @keydown.enter="getData"
                  ></v-text-field>
                </v-col>
                <v-col sm="4">
                  <v-autocomplete
                    solo
                    rounded
                    v-model="filter.is_active"
                    :items="statuses"
                    label="Status"
                    clearable
                    @click:clear="(filter.is_active = null), getData()"
                    @input="getData"
                    prepend-inner-icon="mdi-toggle-switch"
                  ></v-autocomplete>
                </v-col>
              </v-row>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>

      <!-- Data Table Card -->
      <v-row>
        <v-col md="12">
          <v-card elevation="6" rounded="xxl">
            <v-card-text>
              <section v-if="isLoading" style="height: 285px">
                <v-progress-linear
                  indeterminate
                  color="primary"
                ></v-progress-linear>
              </section>
              <v-simple-table
                v-else
                fixed-header
                height="500"
                class="striped-table"
              >
                <template v-slot:default>
                  <thead>
                    <tr>
                      <th class="text-left">Sr No</th>
                      <th class="text-left">Color Name</th>
                      <th class="text-left">Status</th>
                      <th class="text-right">Action</th>
                    </tr>
                  </thead>
                  <tbody v-if="colors.length">
                    <tr v-for="(color, i) in colors" :key="`color_${i}`">
                      <td style="min-width: 85px">
                        {{ (page - 1) * rowsPerPage + i + 1 }}
                      </td>
                      <td>{{ color.description }}</td>
                      <td>
                        <v-chip
                          small
                          :color="color.is_active ? 'teal' : 'red'"
                          text-color="white"
                        >
                          {{ color.is_active ? "Active" : "Inactive" }}
                        </v-chip>
                      </td>
                      <td class="text-right">
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              class="mr-2"
                              color="primary"
                              @click="openEditDialog(color)"
                            >
                              <v-icon dark>mdi-pencil</v-icon>
                            </v-btn>
                          </template>
                          <span>Edit this Color</span>
                        </v-tooltip>
                        <v-tooltip bottom>
                          <template v-slot:activator="{ on, attrs }">
                            <v-btn
                              v-bind="attrs"
                              v-on="on"
                              fab
                              x-small
                              color="error"
                              @click="askDelete(color)"
                            >
                              <v-icon dark>mdi-delete</v-icon>
                            </v-btn>
                          </template>
                          <span>Delete this Color</span>
                        </v-tooltip>
                      </td>
                    </tr>
                  </tbody>
                  <tbody v-else>
                    <tr>
                      <td colspan="4" class="text-center">
                        <v-alert type="info" text>No colors found</v-alert>
                      </td>
                    </tr>
                  </tbody>
                </template>
              </v-simple-table>
              <v-pagination
                v-if="paginationLength > 1"
                v-model="page"
                :length="paginationLength"
              ></v-pagination>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </v-container>

    <!-- Success Dialog -->
    <v-dialog v-model="successDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: green; font-size: 90px">
                mdi-check-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: green">Success!</h1>
                <br />
                <h3>{{ successMessage }}</h3>
                <br />
                <v-btn color="success" @click="successDialog = false">
                  OK
                </v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Error Dialog -->
    <v-dialog v-model="errorDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: red; font-size: 90px">
                mdi-alert-circle
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: red">Error!</h1>
                <br />
                <h3>{{ errorMessage }}</h3>
                <br />
                <v-btn color="error" @click="errorDialog = false">OK</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" width="500">
      <v-card rounded="xxl">
        <v-container>
          <v-row align="center">
            <v-col sm="3" align="center">
              <v-icon large style="color: orange; font-size: 90px">
                mdi-alert
              </v-icon>
            </v-col>
            <v-col sm="9" align="center">
              <v-card-text>
                <h1 style="color: orange">Confirm Delete</h1>
                <br />
                <h3>
                  Delete "{{ deleteTarget ? deleteTarget.description : "" }}"?
                  It will no longer be offered in color dropdowns; existing
                  variants keep their color.
                </h3>
                <br />
                <v-btn
                  color="grey"
                  text
                  class="mr-2"
                  @click="deleteDialog = false"
                >
                  Cancel
                </v-btn>
                <v-btn color="error" @click="confirmDelete">Delete</v-btn>
              </v-card-text>
            </v-col>
          </v-row>
        </v-container>
      </v-card>
    </v-dialog>

    <!-- Create/Edit Dialog -->
    <AppDialog
      :show.sync="dialog"
      max-width="500"
      persistent
      icon="mdi-palette"
      :title="isEditMode ? 'Edit Color' : 'Add Color'"
      :subtitle="isEditMode ? 'Renaming also updates variants using this color' : 'Add a color to offer on products and purchase orders'"
      @close="closeDialog"
    >
      <v-form ref="form" v-model="valid" @submit.prevent="save">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="formData.name"
              label="Color Name *"
              placeholder="e.g. Bottle Green"
              :rules="[rules.required]"
              :error-messages="errors.name"
              hint="Saved in capitals, e.g. BOTTLE GREEN"
              persistent-hint
              outlined
            ></v-text-field>
          </v-col>
          <v-col cols="12">
            <v-switch
              v-model="formData.is_active"
              :label="formData.is_active ? 'Active' : 'Inactive'"
              color="success"
              inset
            ></v-switch>
          </v-col>
        </v-row>
      </v-form>

      <template v-slot:actions>
        <v-btn
          color="primary"
          :loading="isSaving"
          :disabled="!valid || isSaving"
          @click="save"
        >
          {{ isEditMode ? "Update" : "Create" }}
        </v-btn>
      </template>
    </AppDialog>
  </v-main>
</template>

<script>
import axios from "axios";
import AppDialog from "@/components/AppDialog.vue";
export default {
  name: "ColorsPage",
  components: { AppDialog },
  data() {
    return {
      isLoading: false,
      isSaving: false,
      colors: [],
      page: 1,
      paginationLength: 0,
      count: 0,
      dialog: false,
      successDialog: false,
      errorDialog: false,
      deleteDialog: false,
      successMessage: "",
      errorMessage: "",
      deleteTarget: null,
      isEditMode: false,
      valid: false,
      filter: {
        search_keyword: null,
        is_active: null,
      },
      formData: {
        id: null,
        name: "",
        is_active: true,
      },
      statuses: [
        { text: "Active", value: 1 },
        { text: "Inactive", value: 0 },
      ],
      rules: {
        required: (value) => !!(value && value.trim()) || "This field is required",
      },
    };
  },
  computed: {
    errors() {
      return this.$store.getters["errors/getErrors"] || {};
    },
  },
  watch: {
    page: "getData",
  },
  mounted() {
    this.getData();
  },
  methods: {
    async getData() {
      this.isLoading = true;
      try {
        const params = {
          rowsPerPage: this.rowsPerPage,
          page: this.page,
        };
        if (this.filter.search_keyword) {
          params.search_keyword = this.filter.search_keyword;
        }
        if (this.filter.is_active === 0 || this.filter.is_active) {
          params.is_active = this.filter.is_active;
        }

        const response = await axios.get("/colors", { params });
        this.colors = response.data.data || [];
        this.count = response.data.count || 0;
        this.paginationLength = Math.ceil(this.count / this.rowsPerPage);
      } catch (error) {
        console.log(error);
        this.colors = [];
        this.errorMessage = "Failed to load colors.";
        this.errorDialog = true;
      }
      this.isLoading = false;
    },

    openCreateDialog() {
      this.isEditMode = false;
      this.formData = { id: null, name: "", is_active: true };
      this.$store.dispatch("errors/setErrors", {});
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    openEditDialog(color) {
      this.isEditMode = true;
      this.formData = {
        id: color.id,
        name: color.description,
        is_active: !!color.is_active,
      };
      this.$store.dispatch("errors/setErrors", {});
      this.dialog = true;
      if (this.$refs.form) this.$refs.form.resetValidation();
    },

    closeDialog() {
      this.dialog = false;
      this.$store.dispatch("errors/setErrors", {});
    },

    async save() {
      if (!this.$refs.form.validate()) return;

      this.isSaving = true;
      this.$store.dispatch("errors/setErrors", {});
      try {
        const payload = {
          name: this.formData.name,
          is_active: this.formData.is_active,
        };

        if (this.isEditMode) {
          await axios.patch(`/colors/${this.encryptIt(this.formData.id)}`, payload);
          this.successMessage = "Color updated successfully";
        } else {
          await axios.post("/colors", payload);
          this.successMessage = "Color created successfully";
        }

        this.successDialog = true;
        this.closeDialog();
        this.getData();
      } catch (error) {
        console.log(error);
        if (error.response && error.response.data) {
          if (error.response.data.errors) {
            this.$store.dispatch("errors/setErrors", error.response.data.errors);
          } else {
            this.errorMessage = error.response.data.message || "Failed to save color";
            this.errorDialog = true;
          }
        }
      }
      this.isSaving = false;
    },

    askDelete(color) {
      this.deleteTarget = color;
      this.deleteDialog = true;
    },

    async confirmDelete() {
      this.deleteDialog = false;
      try {
        await axios.post(`/colors/delete/${this.encryptIt(this.deleteTarget.id)}`);
        this.successMessage = "Color deleted successfully";
        this.successDialog = true;
        this.getData();
      } catch (error) {
        console.log(error);
        this.errorMessage =
          (error.response && error.response.data && error.response.data.message) ||
          "Failed to delete color";
        this.errorDialog = true;
      }
      this.deleteTarget = null;
    },
  },
};
</script>
